import { useState } from "react";
import { useNavigate, useOutletContext, useSearchParams } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { get, post } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { PageHeader, Section } from "@/components/common/PageHeader";
import { Skeleton, EmptyState } from "@/components/common/EmptyState";
import { StatusBadge } from "@/components/common/StatusBadge";
import { Avatar } from "@/components/common/Avatar";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ListChecks, Plus, ChevronDown, ChevronRight } from "lucide-react";
import { toast } from "sonner";

const PRIORITIES = ["low", "medium", "high", "critical"];

export default function TaskPlanning() {
  const { project } = useOutletContext();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { can } = useApp();
  const [params] = useSearchParams();
  const moduleId = params.get("module");
  const [adding, setAdding] = useState(false);
  const [expanded, setExpanded] = useState({});
  const [templateId, setTemplateId] = useState("");
  const [form, setForm] = useState({ title: "", description: "", acceptance_criteria: "", estimated_hours: "", priority: "medium", assignee_id: "" });

  const { data: module_ } = useQuery({ queryKey: ["item", moduleId], queryFn: () => get(`/pm/items/${moduleId}`), enabled: !!moduleId });
  const { data: epic } = useQuery({ queryKey: ["item", module_?.parent_id], queryFn: () => get(`/pm/items/${module_.parent_id}`), enabled: !!module_?.parent_id });
  const { data: allItems = [], isLoading } = useQuery({ queryKey: ["items", project.id], queryFn: () => get(`/pm/projects/${project.id}/items`) });
  const { data: users = [] } = useQuery({ queryKey: ["users"], queryFn: () => get("/users") });
  const { data: templates = [] } = useQuery({ queryKey: ["task-templates"], queryFn: () => get("/pm/task-templates") });
  const tasks = allItems.filter((i) => i.parent_id === moduleId && i.type === "Task");

  const create = useMutation({
    mutationFn: (body) => post(`/pm/projects/${project.id}/items`, body),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["items", project.id] }); toast.success("Task created"); setAdding(false); setForm({ title: "", description: "", acceptance_criteria: "", estimated_hours: "", priority: "medium", assignee_id: "" }); },
  });
  const applyTemplate = useMutation({
    mutationFn: (tid) => post(`/pm/projects/${project.id}/task-templates/${tid}/apply`, { module_id: moduleId }),
    onSuccess: (created) => { qc.invalidateQueries({ queryKey: ["items", project.id] }); toast.success(`${created.length} tasks created from template`); setTemplateId(""); },
  });
  const moveToBoard = useMutation({
    mutationFn: (wid) => post(`/pm/items/${wid}/move-to-board`, { cascade: true }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["items", project.id] }); toast.success("Moved to board"); },
  });

  return (
    <div>
      <div className="mb-2 text-xs text-muted-foreground">
        <button onClick={() => navigate(`/pm/projects/${project.id}/planning/epics`)} className="hover:underline">Epic Planning</button>
        {epic && <> / <button onClick={() => navigate(`/pm/projects/${project.id}/planning/modules?epic=${epic.id}`)} className="hover:underline">{epic.title}</button></>}
        {module_ && <> / {module_.title} / Tasks</>}
      </div>
      <PageHeader title="Task Planning" subtitle="Actual work the team estimates for this module.">
        {can("pm", "create") && moduleId && (
          <>
            <Select value={templateId} onValueChange={(v) => { setTemplateId(v); applyTemplate.mutate(v); }}>
              <SelectTrigger className="w-56" data-testid="use-template-select"><SelectValue placeholder="Use Template..." /></SelectTrigger>
              <SelectContent>{templates.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}</SelectContent>
            </Select>
            <Button data-testid="add-task-btn" onClick={() => setAdding((s) => !s)}><Plus className="mr-1.5 h-4 w-4" />New Task</Button>
          </>
        )}
      </PageHeader>

      {!moduleId ? (
        <EmptyState icon={ListChecks} title="No module selected" description="Open a task list from Module Planning." />
      ) : isLoading ? <Skeleton className="h-64" /> : (
        <Section>
          {adding && (
            <div className="border-b border-border/60 bg-muted/30 p-4">
              <div className="grid grid-cols-2 gap-3">
                <div className="col-span-2"><Label>Title *</Label><Input data-testid="task-title" className="mt-1" value={form.title} onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))} /></div>
                <div className="col-span-2"><Label>Description</Label><Textarea data-testid="task-description" className="mt-1" value={form.description} onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))} /></div>
                <div className="col-span-2"><Label>Acceptance Criteria</Label><Textarea data-testid="task-ac" className="mt-1" value={form.acceptance_criteria} onChange={(e) => setForm((f) => ({ ...f, acceptance_criteria: e.target.value }))} /></div>
                <div><Label>Estimated Hours</Label><Input data-testid="task-hours" type="number" className="mt-1" value={form.estimated_hours} onChange={(e) => setForm((f) => ({ ...f, estimated_hours: e.target.value }))} /></div>
                <div>
                  <Label>Priority</Label>
                  <Select value={form.priority} onValueChange={(v) => setForm((f) => ({ ...f, priority: v }))}>
                    <SelectTrigger className="mt-1" data-testid="task-priority"><SelectValue /></SelectTrigger>
                    <SelectContent>{PRIORITIES.map((p) => <SelectItem key={p} value={p} className="capitalize">{p}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="col-span-2">
                  <Label>Suggested Assignee (optional — not a final commitment)</Label>
                  <Select value={form.assignee_id} onValueChange={(v) => setForm((f) => ({ ...f, assignee_id: v }))}>
                    <SelectTrigger className="mt-1" data-testid="task-assignee"><SelectValue placeholder="None" /></SelectTrigger>
                    <SelectContent>{users.map((u) => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>
              <div className="mt-3 flex justify-end gap-2">
                <Button size="sm" variant="ghost" onClick={() => setAdding(false)}>Cancel</Button>
                <Button size="sm" data-testid="task-submit" onClick={() => {
                  if (!form.title) { toast.error("Title is required"); return; }
                  create.mutate({ ...form, type: "Task", parent_id: moduleId, in_planning: true, estimated_hours: Number(form.estimated_hours) || 0, assignee_id: form.assignee_id || null });
                }}>Create</Button>
              </div>
            </div>
          )}
          {!tasks.length ? <EmptyState icon={ListChecks} title="No tasks yet" description="Add tasks manually or apply a Task Template." /> : (
            <div className="divide-y divide-border">
              {tasks.map((t) => (
                <div key={t.id}>
                  <div className="flex items-center gap-3 px-4 py-3 text-sm">
                    <button onClick={() => setExpanded((s) => ({ ...s, [t.id]: !s[t.id] }))} className="text-muted-foreground hover:text-foreground">
                      {expanded[t.id] ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                    </button>
                    <button className="flex-1 text-left font-medium hover:underline" onClick={() => navigate(`/pm/projects/${project.id}/items/${t.id}`)}>{t.title}</button>
                    <span className="w-16 text-right text-xs text-muted-foreground">{t.estimated_hours || 0}h</span>
                    <Avatar user={users.find((u) => u.id === t.assignee_id) || { name: "?" }} size="xs" />
                    <StatusBadge status={t.priority} />
                    {t.in_planning && can("pm", "edit") && (
                      <Button size="sm" variant="outline" data-testid={`move-to-board-${t.id}`} onClick={() => moveToBoard.mutate(t.id)}>Move to Board</Button>
                    )}
                  </div>
                  {expanded[t.id] && (
                    <div className="bg-muted/20 px-4 py-2 pl-11 text-xs text-muted-foreground">
                      {t.acceptance_criteria || "No acceptance criteria set."}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </Section>
      )}
    </div>
  );
}
